import React, { useEffect, useId, useRef, useState } from 'react';
import HighlightedTTS from './HighlightedTTS';
import SafeRichContent from './SafeRichContent';
import PassageIllustration from './PassageIllustration';
import { passageImageUrl } from '../utils/passageMedia.js';
import { findCounterArrays } from '../utils/counterModels.js';
import '../styles/passagePresentation.css';
import { resolveReadingPassages } from '../utils/readingPassages';

const isRich = text => /<[a-z][\s\S]*>/i.test(text);

function PassageBody({ passage, ttsEnabled, textSize, fontScale, setFontScale, onMediaFailure }) {
  const [selectedTerm, setSelectedTerm] = useState(null);
  const vocabulary = Array.isArray(passage.Vocabulary) ? passage.Vocabulary : [];
  const text = String(passage.Text || '');
  const title = !passage.Title || passage.Title === 'Reading Passage'
    ? (findCounterArrays(text).length ? 'Array model' : 'Reading Passage') : passage.Title;
  const terms = vocabulary.filter(item => item?.Term && item?.Definition);
  const vocabularyPattern = terms.length && !isRich(text)
    ? new RegExp(`(?<![\\p{L}\\p{N}])(${terms.map(item => item.Term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).sort((a, b) => b.length - a.length).join('|')})(?![\\p{L}\\p{N}])`, 'giu')
    : null;
  const vocabularyRanges = vocabularyPattern
    ? [...text.matchAll(vocabularyPattern)].map(match => ({
      start: match.index,
      end: match.index + match[0].length,
      entry: terms.find(item => item.Term.toLocaleLowerCase() === match[0].toLocaleLowerCase())
    })).filter(range => range.entry)
    : [];
  const typography = { color: '#22201c', fontSize: `calc(${textSize} * ${fontScale})`, lineHeight: 1.72, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' };
  const renderVocabularyTokens = ({ tokens, highlightedStyle, textStyle }) => {
    const rendered = [];
    let index = 0;
    while (index < tokens.length) {
      const token = tokens[index];
      const range = vocabularyRanges.find(candidate => (
        token.rawStart < candidate.end && token.rawEnd > candidate.start
      ));
      if (!range) {
        rendered.push(<span key={`text-${index}`} style={token.isActive ? { ...textStyle, ...highlightedStyle } : textStyle}>{token.text}</span>);
        index += 1;
        continue;
      }
      const grouped = [];
      while (index < tokens.length && tokens[index].rawStart < range.end) {
        grouped.push({ token: tokens[index], index });
        index += 1;
      }
      rendered.push(
        <button
          key={`vocabulary-${range.start}`}
          type="button"
          onClick={() => setSelectedTerm(range.entry)}
          onMouseEnter={() => setSelectedTerm(range.entry)}
          onFocus={() => setSelectedTerm(range.entry)}
          aria-label={`${range.entry.Term}, vocabulary definition`}
          style={{ color: '#075985', border: 0, borderBottom: '2px dotted #0e7490', background: 'transparent', font: 'inherit', padding: 0, cursor: 'pointer' }}
        >
          {grouped.map(({ token: groupedToken, index: tokenIndex }) => (
            <span key={tokenIndex} style={groupedToken.isActive ? { ...textStyle, ...highlightedStyle } : textStyle}>{groupedToken.text}</span>
          ))}
        </button>
      );
    }
    return rendered;
  };
  return <div className="passage-surface" onKeyDown={event => { if (event.key === 'Escape') setSelectedTerm(null); }}>
    <div className="passage-heading">
      <h3>{title}</h3>
      <div className="passage-size-controls" aria-label="Passage text size">
        <span className="passage-size-value" aria-live="polite">{fontScale >= 1.3 ? 'Largest' : fontScale <= .85 ? 'Smallest' : `${Math.round(fontScale * 100)}%`}</span>
        <button type="button" aria-label="Decrease passage text size" disabled={fontScale <= .85} onClick={() => setFontScale(value => Math.max(.85, +(value - .1).toFixed(2)))} style={smallButton}>A−</button>
        <button type="button" aria-label="Increase passage text size" disabled={fontScale >= 1.3} onClick={() => setFontScale(value => Math.min(1.3, +(value + .1).toFixed(2)))} style={smallButton}>A+</button>
      </div>
    </div>
    <PassageIllustration image={passage.Image} alt={passage.ImageAlt || ''} caption={passage.ImageCaption || ''} ttsEnabled={ttsEnabled} onMediaFailure={onMediaFailure} />
    {isRich(text) ? <SafeRichContent html={text} speechText={passage.Speech || ''} ttsEnabled={ttsEnabled} style={typography} />
      : vocabularyRanges.length ? <HighlightedTTS text={text} speechText={passage.Speech || ''} enabled={ttsEnabled} size="small" containerStyle={typography} textStyle={{ color: 'inherit' }} highlightedStyle={{ backgroundColor: '#fef08a' }} renderTokens={renderVocabularyTokens} />
      : <HighlightedTTS text={text} speechText={passage.Speech || ''} enabled={ttsEnabled} size="small" preserveLineBreaks textStyle={typography} highlightedStyle={{ backgroundColor: '#fef08a' }} />}
    {selectedTerm && <div role="dialog" aria-label={`Definition of ${selectedTerm.Term}`} style={{ background: '#eaf6f5', border: '1px solid #99d5cf', borderRadius: 10, padding: 14, marginTop: 18, color: '#153d3a' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}><strong>{selectedTerm.Term}</strong><button type="button" onClick={() => setSelectedTerm(null)} aria-label="Close definition" style={smallButton}>×</button></div>
      {selectedTerm.Pronunciation && <div style={{ fontSize: '.9rem' }}>{selectedTerm.Pronunciation}</div>}
      <div>{selectedTerm.Definition}</div>{selectedTerm.Example && <em style={{ display: 'block', marginTop: 6 }}>{selectedTerm.Example}</em>}
      {selectedTerm.Image && passageImageUrl(selectedTerm.Image) && <img src={passageImageUrl(selectedTerm.Image)} alt={selectedTerm.ImageAlt || ''} style={{ maxWidth: 200, maxHeight: 120, display: 'block', marginTop: 10 }} />}
    </div>}
    {terms.length > 0 && <details style={{ marginTop: 18, color: '#365c58' }}><summary style={{ cursor: 'pointer', fontWeight: 700 }}>Vocabulary ({terms.length})</summary><dl style={{ marginBottom: 0 }}>{terms.map(item => <React.Fragment key={item.Term}><dt style={{ fontWeight: 700, marginTop: 8 }}>{item.Term}</dt><dd style={{ marginLeft: 16 }}>{item.Definition}</dd></React.Fragment>)}</dl></details>}
  </div>;
}
const smallButton = { border: '1px solid #c3d0d0', background: '#fff', borderRadius: 6, padding: '8px 11px', minHeight: 44, minWidth: 44, cursor: 'pointer', color: '#134e4a', fontWeight: 700 };

const passageViewState = new Map();
export default function ReadingPassages({ owner, ttsEnabled = false, textSize = '1.45rem', scopeKey = '', onMediaFailure }) {
  const passages = resolveReadingPassages(owner);
  const [active, setActive] = useState(() => passageViewState.get(scopeKey)?.active || 0);
  const [compare, setCompare] = useState(() => passageViewState.get(scopeKey)?.compare || false);
  const [fontScale, setFontScale] = useState(() => passageViewState.get(scopeKey)?.fontScale || 1.3);
  const tabRefs = useRef([]);
  const panelId = useId();
  useEffect(() => {
    if (scopeKey) passageViewState.set(scopeKey, { active, compare, fontScale });
  }, [scopeKey, active, compare, fontScale]);
  if (!passages.length) return null;
  const shown = compare ? passages : [passages[Math.min(active, passages.length - 1)]];
  const selectTab = index => { setCompare(index === passages.length); if (index < passages.length) setActive(index); };
  const handleTabKey = (event, index) => {
    const last = passages.length;
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % (last + 1);
    else if (event.key === 'ArrowLeft') next = (index + last) % (last + 1);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else return;
    event.preventDefault(); selectTab(next); tabRefs.current[next]?.focus();
  };
  return <section aria-label="Reading passages" style={{ minWidth: 0 }}>
    {passages.length > 1 && <div style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 10, marginBottom: 12, borderBottom: '1px solid #d9cfc0' }} role="tablist" aria-label="Choose a passage">
      {passages.map((passage, index) => <button key={index} id={`${panelId}-tab-${index}`} aria-controls={panelId} ref={node => { tabRefs.current[index] = node; }} type="button" role="tab" aria-selected={!compare && active === index} tabIndex={!compare && active === index ? 0 : -1} onKeyDown={event => handleTabKey(event, index)} onClick={() => selectTab(index)} style={{ ...smallButton, whiteSpace: 'nowrap', background: !compare && active === index ? '#134e4a' : '#fff', color: !compare && active === index ? '#fff' : '#134e4a' }}>{passage.Title}</button>)}
      <button type="button" id={`${panelId}-tab-${passages.length}`} aria-controls={panelId} ref={node => { tabRefs.current[passages.length] = node; }} role="tab" aria-selected={compare} tabIndex={compare ? 0 : -1} onKeyDown={event => handleTabKey(event, passages.length)} onClick={() => selectTab(passages.length)} style={{ ...smallButton, whiteSpace: 'nowrap', background: compare ? '#134e4a' : '#fff', color: compare ? '#fff' : '#134e4a' }}>Compare All</button>
    </div>}
    <div id={panelId} role={passages.length > 1 ? 'tabpanel' : undefined} aria-labelledby={passages.length > 1 ? `${panelId}-tab-${compare ? passages.length : active}` : undefined} tabIndex={passages.length > 1 ? 0 : undefined} style={{ display: 'grid', gridTemplateColumns: compare && passages.length === 2 ? 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))' : '1fr', gap: 14 }}>
      {shown.map((passage, index) => <PassageBody key={passages.indexOf(passage)} passage={passage} ttsEnabled={ttsEnabled} textSize={textSize} fontScale={fontScale} setFontScale={setFontScale} onMediaFailure={onMediaFailure} />)}
    </div>
  </section>;
}
